import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import {
  BadgeColumn,
  IconButtonColumn,
  MultiBadgeColumn,
  PaginationConfig,
  SortableTableComponent,
  TextColumn,
  TextSubtitleColumn,
} from '@vsolv/vectors/table';
import { Staff } from '@wsphere/staff/domain';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ApplyBundleDialog, RoleBundleDialog } from '../../dialogs';
import { RoleBundlesService } from '../../services';

type StatusFilter = 'active' | 'deactivated' | 'all';

/**
 * One source of truth for a page of results. Anything that changes which bundles match must reset `page`,
 * or a filter applied from page 3 lands on an empty page that reads as "no results". Hence `patchCriteria`,
 * rather than a reset at each call site.
 */
interface BundleCriteria {
  page: number;
  limit: number;
  search: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-web-role-bundles-page',
  templateUrl: './role-bundles.page.html',
  standalone: false,
})
export class RoleBundlesPage extends SortableTableComponent implements OnInit, OnDestroy {
  constructor(
    private bundleSvc: RoleBundlesService,
    private toastSvc: ToastService
  ) {
    super();
  }

  @ViewChild(RoleBundleDialog) bundleDialog?: RoleBundleDialog;
  @ViewChild(ApplyBundleDialog) applyDialog?: ApplyBundleDialog;

  readonly searchControl = new FormControl('', { nonNullable: true });

  readonly loading$ = new BehaviorSubject(true);

  private readonly destroy$ = new Subject<void>();
  private readonly criteria$ = new BehaviorSubject<BundleCriteria>({
    page: 1,
    limit: 10,
    search: '',
    isActive: true,
  });

  private refresh$ = new BehaviorSubject<null>(null);

  // String-keyed: vs-select cannot round-trip an `undefined` option value.
  statusFilter: StatusFilter = 'active';
  statuses: { label: string; value: StatusFilter }[] = [
    { label: 'Active', value: 'active' },
    { label: 'Deactivated', value: 'deactivated' },
    { label: 'All', value: 'all' },
  ];

  ngOnInit() {
    this.searchControl.valueChanges
      .pipe(
        // Before the debounce, so typing feels responsive.
        tap(() => this.loading$.next(true)),
        debounceTime(300),
        map(search => search.trim()),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(search => this.patchCriteria({ search }));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private paginatedData$ = combineLatest([
    this.criteria$.pipe(tap(() => this.loading$.next(true))),
    this.refresh$,
  ]).pipe(
    switchMap(async ([criteria]) => {
      try {
        return await this.bundleSvc.list({
          page: criteria.page,
          limit: criteria.limit,
          ...(criteria.search && { search: criteria.search }),
          ...(criteria.isActive !== undefined && { isActive: criteria.isActive }),
          ...(criteria.sortBy && criteria.sortOrder && { sortBy: criteria.sortBy, sortOrder: criteria.sortOrder }),
        });
      } catch {
        // Caught here, not with catchError: that would tear down the stream on the first failure.
        this.toastSvc.show({
          type: 'error',
          title: 'Failed to load bundles',
          text: 'Please try again or contact support.',
        });
        return null;
      }
    }),
    tap(() => this.loading$.next(false)),
    // refCount so the source is released when the page is destroyed, not kept alive by the replay buffer.
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly bundles$: Observable<Staff.Security.RoleBundle[]> = this.paginatedData$.pipe(
    map(data => data?.items ?? []),
    startWith([])
  );

  readonly paginationConfig$: Observable<Partial<PaginationConfig>> = this.paginatedData$.pipe(
    startWith(null),
    map(data => ({
      currentPage: data?.meta.currentPage ?? this.criteria$.value.page,
      itemsPerPage: data?.meta.itemsPerPage ?? this.criteria$.value.limit,
      totalItems: data?.meta.totalItems ?? 0,
      pageSizes: [10, 20, 50],
    }))
  );

  // Sortable headers must match what the list handler accepts: Name, Status, Created.
  readonly tableColumns = [
    new TextSubtitleColumn<Staff.Security.RoleBundle>({ header: 'Name', sortable: true }, bundle => ({
      title: bundle.name,
      subtitle: bundle.description,
    })),

    new MultiBadgeColumn<Staff.Security.RoleBundle>({ header: 'Roles' }, bundle => ({
      limit: 3,
      items: (bundle.items ?? []).map(item => {
        const role = Staff.Security.getRole(item.roleKey);
        return { text: role?.display.title ?? item.roleKey, theme: role?.display.theme ?? 'default' };
      }),
    })),

    new BadgeColumn<Staff.Security.RoleBundle>({ header: 'Status', sortable: true }, bundle =>
      bundle.isActive
        ? { text: 'Active', theme: 'success', displayStatusIcon: true }
        : { text: 'Deactivated', theme: 'danger', displayStatusIcon: true }
    ),

    new BadgeColumn<Staff.Security.RoleBundle>({ header: 'Type' }, bundle =>
      bundle.isSystem ? { text: 'System', theme: 'primary' } : { text: 'Custom', theme: 'default' }
    ),

    new TextColumn<Staff.Security.RoleBundle>({ header: 'Created', fitContent: true, sortable: true }, bundle => ({
      text: moment(bundle.created).format('MMM DD, YYYY'),
      classes: 'text-center',
    })),

    // System bundles are fixed (also rejected server-side); duplicating gives an editable copy.
    new IconButtonColumn<Staff.Security.RoleBundle>({ stickyEnd: true, fitContent: true }, bundle => ({
      type: 'clear',
      rounded: true,
      icon: 'edit-01',
      disabled: bundle.isSystem,
      tooltipText: bundle.isSystem ? 'System bundles cannot be edited. Duplicate it to make changes.' : 'Edit bundle',
      click: () => this.edit(bundle),
    })),

    // Editing a bundle never touches existing members; this is the explicit push. A system bundle cannot be
    // edited, so its members can never fall behind it and applying would always be a no-op.
    new IconButtonColumn<Staff.Security.RoleBundle>({ stickyEnd: true, fitContent: true }, bundle => ({
      type: 'clear',
      rounded: true,
      icon: 'users-check',
      disabled: bundle.isSystem,
      tooltipText: bundle.isSystem
        ? 'System bundles cannot be edited, so members are always up to date.'
        : 'Apply to members',
      click: () => this.applyDialog?.open(bundle),
    })),

    new IconButtonColumn<Staff.Security.RoleBundle>({ stickyEnd: true, fitContent: true }, bundle => ({
      type: 'clear',
      rounded: true,
      icon: 'copy-01',
      tooltipText: 'Duplicate bundle',
      click: () => this.duplicate(bundle),
    })),

    new IconButtonColumn<Staff.Security.RoleBundle>({ stickyEnd: true, fitContent: true }, bundle => ({
      type: 'clear',
      rounded: true,
      icon: bundle.isActive ? 'slash-circle-01' : 'refresh-ccw-04',
      disabled: bundle.isSystem,
      tooltipText: bundle.isSystem
        ? 'System bundles cannot be deactivated.'
        : bundle.isActive
          ? 'Deactivate bundle'
          : 'Reactivate bundle',
      click: () => this.setActive(bundle, !bundle.isActive),
    })),
  ];

  /** Applies a criteria change and returns to page 1: the result set is no longer the same. */
  private patchCriteria(change: Partial<Omit<BundleCriteria, 'page'>>) {
    this.criteria$.next({ ...this.criteria$.value, ...change, page: 1 });
  }

  override onSortChanged(sort: { sortBy: string; sortOrder: 'ASC' | 'DESC' } | null) {
    super.onSortChanged(sort);
    this.patchCriteria({ sortBy: sort?.sortBy, sortOrder: sort?.sortOrder });
  }

  onPaginationChanged(pagination: { currentPage: number; itemsPerPage: number }) {
    const { page, limit } = this.criteria$.value;
    if (pagination.itemsPerPage !== limit) {
      // A different page size makes the old page number meaningless.
      this.patchCriteria({ limit: pagination.itemsPerPage });
    } else if (pagination.currentPage !== page) {
      this.criteria$.next({ ...this.criteria$.value, page: pagination.currentPage });
    }
  }

  filterByStatus(status: StatusFilter) {
    this.statusFilter = status;
    this.patchCriteria({ isActive: status === 'all' ? undefined : status === 'active' });
  }

  refresh() {
    this.refresh$.next(null);
  }

  create() {
    this.bundleDialog?.open();
  }

  async edit(bundle: Staff.Security.RoleBundle) {
    try {
      // Re-retrieve: the listed row may be stale.
      this.bundleDialog?.open(await this.bundleSvc.retrieve(bundle.id));
    } catch {
      this.toastSvc.show({ type: 'error', title: 'Failed to load bundle', text: 'Please try again.' });
    }
  }

  async duplicate(bundle: Staff.Security.RoleBundle) {
    try {
      await this.bundleSvc.duplicate(bundle.id);
      this.toastSvc.show({ type: 'success', title: 'Bundle duplicated', text: `Copied ${bundle.name}.` });
      this.refresh();
    } catch {
      this.toastSvc.show({ type: 'error', title: 'Failed to duplicate bundle', text: 'Please try again.' });
    }
  }

  async setActive(bundle: Staff.Security.RoleBundle, isActive: boolean) {
    try {
      await this.bundleSvc.setActive(bundle.id, isActive);
      this.toastSvc.show({
        type: 'success',
        title: isActive ? 'Bundle reactivated' : 'Bundle deactivated',
        // Deactivating only hides it from new assignments; staff keep the roles it granted.
        text: isActive
          ? `${bundle.name} can be assigned again.`
          : `${bundle.name} is hidden from new assignments. Members keep the roles it granted.`,
      });
      this.refresh();
    } catch {
      this.toastSvc.show({ type: 'error', title: 'Failed to update bundle', text: 'Please try again.' });
    }
  }
}
